import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Container, Paper, Text, TextInput, Button, Stack, Alert } from '@mantine/core'
import { IconKey, IconAlertCircle, IconLock } from '@tabler/icons-react'
import { ResponsiveTitle } from '../../components/ResponsiveTitle/ResponsiveTitle'
import { supabase } from '@/lib/supabase'
import { isValidPin } from '@/lib/utils'
import { getDeviceId } from '@/lib/deviceId'
import { useSiteContent } from '@/hooks/useSiteContent'
import styles from './pin.module.css'

export const Route = createFileRoute('/guest/pin')({
  component: GuestPinComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      pin: (search.pin as string) || undefined,
    }
  },
})

function GuestPinComponent() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const { content } = useSiteContent()
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isLockedOut, setIsLockedOut] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)

  // Check lockout status on mount
  useEffect(() => {
    const checkLockoutStatus = async () => {
      try {
        const deviceId = getDeviceId()
        const { data } = await supabase
          .from('login_attempts')
          .select('lockout_until')
          .eq('device_id', deviceId)
          .eq('lockout_type', 'guest_pin_lockout')
          .maybeSingle()

        if (data?.lockout_until) {
          const lockoutUntil = new Date(data.lockout_until).getTime()
          const now = Date.now()
          if (now < lockoutUntil) {
            setIsLockedOut(true)
            const updateCountdown = () => {
              const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000))
              setRemainingSeconds(remaining)
              if (remaining === 0) {
                setIsLockedOut(false)
              }
            }
            updateCountdown()
            const interval = setInterval(updateCountdown, 1000)
            return () => clearInterval(interval)
          }
        }
      } catch (err) {
        console.error('Error checking lockout status:', err)
      }
    }
    checkLockoutStatus()
  }, [])

  // Auto-validate PIN from URL parameter
  useEffect(() => {
    if (search.pin && !isLockedOut) {
      const urlPin = search.pin
      if (isValidPin(urlPin)) {
        setPin(urlPin)
        validateAndNavigate(urlPin)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.pin, isLockedOut])

  const validateAndNavigate = async (pinToValidate: string) => {
    if (isLockedOut) {
      return
    }

    setLoading(true)
    setError('')

    try {
      const deviceId = await getDeviceId()

      // Call database function that handles PIN validation and lockout
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'validate_guest_pin_with_lockout',
        {
          p_pin: pinToValidate,
          p_device_id: deviceId,
          p_lockout_type: 'guest_pin_lockout',
        }
      )

      if (rpcError) {
        console.error('Error validating PIN:', rpcError)
        setError('Ein Fehler ist aufgetreten')
        return
      }

      // Type assertion for the JSONB response
      const data = rpcData as {
        success: boolean
        error?: 'LOCKED_OUT' | 'INVALID_PIN'
        lockout_until?: string
        remaining_seconds?: number
        guest_id?: string
        primary_name?: string
      }

      if (data.success) {
        // PIN is valid, navigate to invitation
        navigate({ to: `/guest/logged_in/${pinToValidate}/invitation` })
      } else if (data.error === 'LOCKED_OUT') {
        // Locked out - show countdown
        setIsLockedOut(true)
        if (data.lockout_until) {
          const lockoutUntil = new Date(data.lockout_until).getTime()
          const updateCountdown = () => {
            const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000))
            setRemainingSeconds(remaining)
            if (remaining === 0) {
              setIsLockedOut(false)
            }
          }
          updateCountdown()
          const interval = setInterval(updateCountdown, 1000)
          // Store interval to clear on unmount
          setTimeout(() => clearInterval(interval), lockoutUntil - Date.now() + 1000)
        }
      } else if (data.error === 'INVALID_PIN') {
        // Invalid PIN but not locked out yet
        setError('PIN nicht gefunden')
      } else {
        setError('Ein Fehler ist aufgetreten')
      }
    } catch (err) {
      console.error('Error validating PIN:', err)
      setError('Ein Fehler ist aufgetreten')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLockedOut) {
      return
    }

    setError('')

    if (!isValidPin(pin)) {
      setError('Bitte geben Sie Ihren 5-stelligen PIN-Code ein')
      return
    }

    await validateAndNavigate(pin)
  }

  return (
    <div className={`guest-layout`}>
      <Container size="sm" py="xl" className={styles.container}>
        <Paper className={styles.paper} shadow="xl" p="xl">
          <Stack gap="lg">
            <ResponsiveTitle order={1} ta="center" className={styles.title}>
              {content?.event.coupleNames || 'Jan und Michael'}
            </ResponsiveTitle>

            <Text ta="center" size="xl" className={styles.subtitle}>
              laden Dich herzlich ein zur
              <br />
              <strong>{content?.event.eventTitle || 'Freien Hochzeit'}</strong>
            </Text>

            <Text ta="center" size="lg" c="dimmed">
              {content?.event.eventDate || '19.09.2026'}
            </Text>

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label={content?.ui['PIN eingeben'] || 'PIN eingeben'}
                  placeholder="12345"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  maxLength={5}
                  leftSection={<IconKey size={16} />}
                  size="lg"
                  required
                  autoFocus
                  inputMode="numeric"
                  pattern="\d{5}"
                  disabled={isLockedOut}
                />

                {isLockedOut ? (
                  <Alert
                    icon={<IconLock size={16} />}
                    color="red"
                    title="Zu viele fehlgeschlagene Versuche"
                  >
                    {remainingSeconds > 0
                      ? `Sie wurden für ${remainingSeconds} Sekunden gesperrt. Bitte versuchen Sie es später erneut.`
                      : 'Sie wurden für 1 Minute gesperrt. Bitte versuchen Sie es später erneut.'}
                  </Alert>
                ) : (
                  <>
                    {error && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        title="PIN nicht gefunden"
                      >
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      fullWidth
                      loading={loading}
                      className={styles.submitButton}
                    >
                      {content?.ui.Weiter || 'Weiter'}
                    </Button>
                  </>
                )}
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Container>
    </div>
  )
}
